import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Row, Col, Select, Spin, Empty, Statistic, Progress } from 'antd';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ResponsiveContainer, RadialBarChart, RadialBar,
} from 'recharts';
import { ReloadOutlined, BarChartOutlined } from '@ant-design/icons';
import { getTeleradiologyAnalytics } from './TeleradiologyService';

const { Option } = Select;

const TEAL       = '#2a5f5a';
const STAT_COLOR = '#dc2626';
const URG_COLOR  = '#d97706';
const ROU_COLOR  = '#16a34a';
const BLUE       = '#3b82f6';
const PURPLE     = '#7c3aed';
const MODALITY_COLORS = ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#10b981','#06b6d4','#f97316','#6366f1'];
const GROUP_NAMES = { group_a: 'Group A', group_b: 'Group B', group_c: 'Group C' };

const fmtTAT = (min) => {
  if (!min || min < 1) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const Card = ({ children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: 10,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0,0,0,.04)',
    padding: '12px 16px', height: '100%',
    ...style,
  }}>{children}</div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>{children}</div>
);

const DarkTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1f2937', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#fff' }}>
      {label && <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const TeleradiologyAnalytics = ({ profile }) => {
  const [days, setDays]       = useState(30);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const roles      = profile?.realm_roles || [];
  const hospitalId = profile?.hospital_id  || '';
  const isTele     = hospitalId === 'teleradiology';
  const isCoord    = isTele && roles.some(r => ['PO', 'PROJECT_OWNER', 'MANAGER', 'ADMIN'].includes(r));

  const loadData = useCallback(() => {
    setLoading(true);
    getTeleradiologyAnalytics(days)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isCoord) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
        <BarChartOutlined style={{ fontSize: 40, marginBottom: 12 }} />
        <p>Only dispatch coordinators can view analytics.</p>
      </div>
    );
  }

  const ov  = data?.overview || {};
  const sla = data?.sla      || {};
  const completionRate = ov.total > 0 ? Math.round((ov.completed / ov.total) * 100) : 0;

  const urgencyData = [
    { name: 'STAT',    count: sla.STAT?.total    || 0, tat: Math.round(sla.STAT?.avg_tat_min    || 0), sla: Math.round(sla.STAT?.sla_percent    || 0), color: STAT_COLOR },
    { name: 'Urgent',  count: sla.URGENT?.total  || 0, tat: Math.round(sla.URGENT?.avg_tat_min  || 0), sla: Math.round(sla.URGENT?.sla_percent  || 0), color: URG_COLOR  },
    { name: 'Routine', count: sla.ROUTINE?.total || 0, tat: Math.round(sla.ROUTINE?.avg_tat_min || 0), sla: Math.round(sla.ROUTINE?.sla_percent || 0), color: ROU_COLOR  },
  ];

  const byDate      = (data?.by_date    || []).map(d => ({ ...d, date: d.date.slice(5) }));
  const byModality  = (data?.by_modality  || []).sort((a, b) => b.total - a.total);
  const byHospital  = (data?.by_hospital  || []).sort((a, b) => b.total - a.total);
  const radiologists= (data?.radiologists || []).sort((a, b) => b.completed - a.completed);

  let totalTAT = 0, tatN = 0;
  urgencyData.forEach(u => { if (u.count > 0) { totalTAT += u.tat * u.count; tatN += u.count; } });
  const avgTAT = tatN > 0 ? totalTAT / tatN : 0;

  return (
    <div style={{ padding: '12px 20px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#111827' }}>
            <BarChartOutlined style={{ marginRight: 8, color: TEAL }} />
            Teleradiology Analytics
          </h1>
          <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: 12 }}>
            Performance metrics, SLA compliance, radiologist activity
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select value={days} onChange={v => setDays(v)} style={{ width: 130 }} size="small">
            <Option value={7}>Last 7 days</Option>
            <Option value={14}>Last 14 days</Option>
            <Option value={30}>Last 30 days</Option>
            <Option value={90}>Last 90 days</Option>
            <Option value={365}>Last 365 days</Option>
          </Select>
          <button onClick={loadData} style={{
            border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff',
            padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#374151',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <ReloadOutlined spin={loading} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#6b7280', fontSize: 13 }}>Loading analytics...</div>
        </div>
      ) : !data ? (
        <Empty description="Failed to load analytics" />
      ) : (
        <>
          {/* ── Row 1: KPI cards (6 columns) ── */}
          <Row gutter={10} style={{ marginBottom: 10 }}>
            {[
              { label: 'Total Studies',  value: ov.total,        color: TEAL    },
              { label: 'Completed',      value: ov.completed,    color: ROU_COLOR, sub: `${completionRate}% rate` },
              { label: 'In Progress',    value: ov.in_progress,  color: BLUE    },
              { label: 'Pending',        value: ov.pending,      color: '#6b7280' },
              { label: 'Avg TAT',        value: fmtTAT(avgTAT),  color: PURPLE  },
              { label: 'Cancelled',      value: ov.cancelled,    color: STAT_COLOR },
            ].map(m => (
              <Col key={m.label} span={4}>
                <Card style={{ padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{m.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: m.color, lineHeight: 1.2 }}>{m.value ?? '—'}</div>
                  {m.sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{m.sub}</div>}
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Row 2: Daily trend + SLA ── */}
          <Row gutter={10} style={{ marginBottom: 10 }}>
            <Col span={16}>
              <Card>
                <SectionTitle>Daily Volume — Submitted vs Completed</SectionTitle>
                <ResponsiveContainer width="100%" height={170}>
                  <AreaChart data={byDate} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={BLUE} stopOpacity={0.2} /><stop offset="95%" stopColor={BLUE} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={TEAL} stopOpacity={0.25} /><stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
                    <RTooltip content={<DarkTip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="submitted" name="Submitted" stroke={BLUE} fill="url(#gS)" strokeWidth={2} dot={false} />
                    <Area type="monotone" dataKey="completed" name="Completed" stroke={TEAL} fill="url(#gC)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <SectionTitle>SLA Compliance</SectionTitle>
                <ResponsiveContainer width="100%" height={100}>
                  <RadialBarChart innerRadius="25%" outerRadius="90%" data={urgencyData.map(u => ({ name: u.name, value: u.sla, fill: u.color }))} startAngle={90} endAngle={-270}>
                    <RadialBar minAngle={5} dataKey="value" cornerRadius={4} background={{ fill: '#f3f4f6' }} />
                    <RTooltip formatter={(v) => [`${v}%`, 'SLA']} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {urgencyData.map(u => (
                    <div key={u.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                        <span style={{ color: u.color, fontWeight: 600 }}>{u.name}</span>
                        <span style={{ color: '#6b7280' }}>{u.sla}% · {fmtTAT(u.tat)} avg</span>
                      </div>
                      <Progress percent={u.sla} showInfo={false} strokeColor={u.color} trailColor="#f3f4f6" size="small" />
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          {/* ── Row 3: Urgency + Hospital + Modality (3 equal columns) ── */}
          <Row gutter={10} style={{ marginBottom: 10 }}>
            <Col span={8}>
              <Card>
                <SectionTitle>By Urgency Tier</SectionTitle>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={urgencyData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <RTooltip content={<DarkTip />} />
                    <Bar dataKey="count" name="Studies" radius={[3, 3, 0, 0]}>
                      {urgencyData.map((u, i) => <Cell key={i} fill={u.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <SectionTitle>By Source Hospital</SectionTitle>
                {byHospital.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={byHospital} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                      <YAxis type="category" dataKey="hospital_name" tick={{ fontSize: 11 }} width={70} />
                      <RTooltip content={<DarkTip />} />
                      <Bar dataKey="total" name="Total" fill={BLUE} radius={[0, 3, 3, 0]} />
                      <Bar dataKey="completed" name="Done" fill={TEAL} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <SectionTitle>By Modality</SectionTitle>
                {byModality.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                  <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                      <Pie data={byModality} dataKey="total" nameKey="modality" cx="50%" cy="50%" outerRadius={60} innerRadius={35} paddingAngle={2}>
                        {byModality.map((_, i) => <Cell key={i} fill={MODALITY_COLORS[i % MODALITY_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v, n) => [v, n]} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
          </Row>

          {/* ── Row 4: Radiologist table ── */}
          <Row gutter={10}>
            <Col span={24}>
              <Card>
                <SectionTitle>Radiologist Performance</SectionTitle>
                {radiologists.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No completed readings yet" />
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        {['Radiologist', 'Group', 'Completed', 'Avg TAT', 'STAT', 'Urgent', 'Routine'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {radiologists.map((r, i) => (
                        <tr key={r.assignee_id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '7px 10px', fontWeight: 600, color: '#111827' }}>{r.assignee_username}</td>
                          <td style={{ padding: '7px 10px', color: TEAL, fontSize: 11 }}>{GROUP_NAMES[r.group_id] || r.group_id}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 700, color: TEAL, fontSize: 15 }}>{r.completed}</td>
                          <td style={{ padding: '7px 10px', color: '#374151' }}>{fmtTAT(r.avg_tat_min)}</td>
                          <td style={{ padding: '7px 10px' }}><span style={{ background: '#fef2f2', color: STAT_COLOR, borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>{r.by_urgency?.STAT || 0}</span></td>
                          <td style={{ padding: '7px 10px' }}><span style={{ background: '#fffbeb', color: URG_COLOR, borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>{r.by_urgency?.URGENT || 0}</span></td>
                          <td style={{ padding: '7px 10px' }}><span style={{ background: '#f0fdf4', color: ROU_COLOR, borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>{r.by_urgency?.ROUTINE || 0}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({ profile: state.system.profile });
export default connect(mapStateToProps)(withRouter(TeleradiologyAnalytics));
